import React, { useEffect, useMemo } from 'react';
import { useSelector } from 'react-redux';
import { Form } from '@unform/web';
import {
  Button,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableRow,
  TextField,
} from '@material-ui/core';
import AddIcon from '@material-ui/icons/Add';
import DeleteIcon from '@material-ui/icons/Delete';
import { Spinner } from '~/components';
import {
  Input,
  DateTime,
  Select,
  Hidden,
  Autocomplete,
  ComboboxMultiple,
} from '~/components/Form';
import { isEmpty, isArray } from 'lodash';
import { toast } from 'react-toastify';
import qs from 'querystring';
import * as Yup from 'yup';
import { parse } from 'date-fns';
import { t, validCharacters } from '~/helpers';
import api from '~/services/api';
import { Container } from './styles';
import { MODULO_CUSTOMIZACAO_RUMO } from '~/constants/modules';

export default function AreiaPitStop({
  data,
  reference,
  close,
  cancelarAcao,
  finalizaGravacao,
}) {
  const { client } = useSelector((state) => state.user.profile);
  const inputRef = React.useRef(null);
  const [initials, setInitials] = React.useState(null);
  const [idatendimento, setIdatendimento] = React.useState(null);
  const [co, setCo] = React.useState(null);
  const [idposto, setIdposto] = React.useState(null);
  const [atendeu, setAtendeu] = React.useState('S');
  const [tiposAreia, setTiposAreia] = React.useState([]);
  const [areiaItens, setAreiaItens] = React.useState([]);
  const modules = useSelector((state) => state.user.modules);
  const clienteRumo = modules.includes(MODULO_CUSTOMIZACAO_RUMO);

  const optionsAtendimento = useMemo(
    () => [
      { value: 'S', label: t('s.geral.opcoes.sim') },
      { value: 'N', label: t('s.geral.opcoes.nao') },
      { value: 'X', label: t('s.geral.opcoes.na') },
    ],
    []
  );

  const totalPesoAreia = useMemo(
    () =>
      areiaItens.reduce((total, item) => {
        const calculo = calculaItem(item);
        return total + Number(calculo.pesototalareiaitem || 0);
      }, 0),
    [areiaItens, tiposAreia]
  );

  useEffect(() => {
    async function handleCarregaTiposAreia() {
      const response = await api.get('/estoqueareia', {
        params: {
          wherefield: ['idposto'],
          valuewherefield: [idposto],
          typewherefield: ['EQUALS'],
          pagesize: 99999,
          pesquisalista: true,
        },
        paramsSerializer: (param) => qs.stringify(param),
      });
      setTiposAreia(response.data.items || []);
    }
    handleCarregaTiposAreia();
  }, [idposto]);

  useEffect(() => {
    async function handleCarregaAtendimento(idatendimento) {
      const { data } = await api.get(`/pitstop/${idatendimento}`);
      setIdatendimento(data.idatendimento);
      setCo(data.codigoatendimento);
      setIdposto(data.idposto);
    }
    if (data != null) {
      handleCarregaAtendimento(data.idatendimento);
    }
  }, [data]);

  useEffect(() => {
    async function handleCarregaAreia(idatendimento) {
      const { data } = await api.get(`/pitstop/areia/${idatendimento}`);
      setAtendeu(data.atendeuatendimentoareia);
      setAreiaItens(consolidaItensAreia(data.itens || []));
      setInitials(data);
      reference.current.setData(data);
    }
    if (idatendimento) {
      handleCarregaAreia(idatendimento);
    }
  }, [idatendimento]);

  function getTipoAreia(idpostotipoareia) {
    return tiposAreia.find(
      (tipo) => tipo.idpostotipoareia === Number(idpostotipoareia)
    );
  }

  function calculaItem(item) {
    const tipoAreia = getTipoAreia(item.idpostotipoareia);
    const quantidade = Number(item.quantidadeareiaitem || 0);

    if (!tipoAreia) {
      return {
        duracaoareiaitem: item.duracaoareiaitem,
        pesounitarioareiaitem: item.pesounitarioareiaitem,
        pesototalareiaitem: item.pesototalareiaitem,
      };
    }

    if (tipoAreia.silotipoareia === 'S') {
      const quantidadeInteira = parseInt(quantidade, 10) || 0;

      return {
        duracaoareiaitem: quantidadeInteira,
        pesounitarioareiaitem: 1,
        pesototalareiaitem:
          quantidadeInteira * tipoAreia.fatorconversaovazaotipoareia,
      };
    }

    const pesoUnitario = Number(tipoAreia.pesounitariokgtipoareia || 0);

    return {
      duracaoareiaitem: null,
      pesounitarioareiaitem: pesoUnitario,
      pesototalareiaitem: quantidade * pesoUnitario,
    };
  }

  function isQuantidadePreenchida(value) {
    return value !== null && value !== undefined && value !== '';
  }

  function isQuantidadeValida(value) {
    return isQuantidadePreenchida(value) && Number(value) > 0;
  }

  function isItemAreiaIncompleto(item) {
    return (
      !item.idpostotipoareia || !isQuantidadeValida(item.quantidadeareiaitem)
    );
  }

  function consolidaItensAreia(itens) {
    const itensSemTipo = [];
    const itensPorTipo = new Map();

    itens.forEach((item) => {
      if (!item.idpostotipoareia) {
        itensSemTipo.push(item);
        return;
      }

      const idpostotipoareia = Number(item.idpostotipoareia);
      const itemExistente = itensPorTipo.get(idpostotipoareia);

      if (!itemExistente) {
        const nextItem = {
          ...item,
          idpostotipoareia,
        };

        itensPorTipo.set(idpostotipoareia, {
          ...nextItem,
          ...calculaItem(nextItem),
        });
        return;
      }

      const quantidadeAtual = Number(itemExistente.quantidadeareiaitem || 0);
      const quantidadeNova = Number(item.quantidadeareiaitem || 0);
      const nextItem = {
        ...itemExistente,
        quantidadeareiaitem: quantidadeAtual + quantidadeNova,
      };

      itensPorTipo.set(idpostotipoareia, {
        ...nextItem,
        ...calculaItem(nextItem),
      });
    });

    return [...itensPorTipo.values(), ...itensSemTipo];
  }

  function handleAddItem() {
    setAreiaItens((state) => [
      ...state,
      {
        idpostotipoareia: '',
        quantidadeareiaitem: '',
        duracaoareiaitem: null,
        pesounitarioareiaitem: 0,
        pesototalareiaitem: 0,
      },
    ]);
  }

  function handleChangeItem(index, field, value) {
    setAreiaItens((state) => {
      const nextState = state.map((item, idx) => {
        if (idx !== index) return item;

        const nextItem = { ...item, [field]: value };

        return {
          ...nextItem,
          ...calculaItem(nextItem),
        };
      });

      return consolidaItensAreia(nextState);
    });
  }

  function handleRemoveItem(index) {
    setAreiaItens((state) => state.filter((_, idx) => idx !== index));
  }

  function preparaItensAreia() {
    return consolidaItensAreia(areiaItens).map((item) => ({
      ...item,
      idpostotipoareia: Number(item.idpostotipoareia),
      quantidadeareiaitem: Number(item.quantidadeareiaitem),
      ...calculaItem(item),
    }));
  }

  async function handleSubmit(param) {
    try {
      if (param.atendeuatendimentoareia === 'S' && clienteRumo === false) {
        const possuiItens = areiaItens.length > 0;
        const possuiItemIncompleto = areiaItens.some(isItemAreiaIncompleto);

        if (!possuiItens || possuiItemIncompleto) {
          await finalizaGravacao();
          await cancelarAcao();
          toast.error('Preencha de forma completa a quantidade de areia');
          return;
        }
      }

      const schemaS = Yup.object().shape({
        atendeuatendimentoareia: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        datainicioatendimentoareia: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        datafimatendimentoareia: Yup.mixed()
          .transform((value) =>
            value ? parse(value, 'dd/MM/yyyy HH:mm:ss', new Date()) : null
          )
          .required(t('s.geral.validacao.campoobrigatorio')),
        sacosatendimentoareia: clienteRumo
          ? Yup.number()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .transform((value) => (isNaN(value) ? undefined : value))
              .integer(t('s.geral.validacao.deveserinteiro'))
              .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
              .lessThan(
                50001,
                t('s.geral.validacao.valormaximo').replace('%s', 50000)
              )
          : Yup.mixed().nullable(),
        itens: clienteRumo
          ? Yup.mixed().nullable()
          : Yup.array()
              .of(
                Yup.object().shape({
                  idpostotipoareia: Yup.number()
                    .required(t('s.geral.validacao.campoobrigatorio'))
                    .typeError(t('s.geral.validacao.campoobrigatorio')),
                  quantidadeareiaitem: Yup.number()
                    .required(t('s.geral.validacao.campoobrigatorio'))
                    .transform((value) => (isNaN(value) ? undefined : value))
                    .min(0, t('s.geral.validacao.valorminimo').replace('%s', 0))
                    .lessThan(
                      50001,
                      t('s.geral.validacao.valormaximo').replace('%s', 50000)
                    ),
                })
              )
              .min(1, t('s.geral.validacao.campoobrigatorio')),
        observacaoareia: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });

      const schemaN = Yup.object().shape({
        atendeuatendimentoareia: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        justificativas: Yup.array().required(
          t('s.geral.validacao.campoobrigatorio')
        ),
        observacaoareia: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });

      const schemaX = Yup.object().shape({
        atendeuatendimentoareia: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
        observacaoareia: Yup.string()
          .ensure()
          .nullable()
          .trim()
          .required(t('s.geral.validacao.campoobrigatorio'))
          .max(500, t('s.geral.validacao.tamanhomaximo').replace('%s', 500))
          .test(
            'characters-invalid',
            t('s.configuracao.geral.naopodecontercaracterespecial'),
            function (value) {
              return validCharacters(value);
            }
          ),
      });

      const schema = Yup.object().shape({
        atendeuatendimentoareia: Yup.string()
          .nullable()
          .required(t('s.geral.validacao.campoobrigatorio')),
        codigousuario: clienteRumo
          ? Yup.string().nullable()
          : Yup.string()
              .required(t('s.geral.validacao.campoobrigatorio'))
              .typeError(t('s.geral.validacao.campoobrigatorio'))
              .max(100, t('s.geral.validacao.valormaximo').replace('%s', 100)),
      });

      const itens =
        param.atendeuatendimentoareia === 'S' && clienteRumo === false
          ? preparaItensAreia()
          : [];

      const paramValidate = { ...param, itens };

      if (param.atendeuatendimentoareia === 'S') {
        await schemaS.validate(paramValidate, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentoareia === 'N') {
        await schemaN.validate(param, {
          abortEarly: false,
        });
      } else if (param.atendeuatendimentoareia === 'X') {
        await schemaX.validate(param, {
          abortEarly: false,
        });
      } else {
        await schema.validate(param, {
          abortEarly: false,
        });
      }

      const objSend = {
        ...initials,
        ...param,
        datainicioatendimentoareia:
          param.datainicioatendimentoareia ||
          initials.datainicioatendimentoareia,
        datafimatendimentoareia:
          param.datafimatendimentoareia || initials.datafimatendimentoareia,
        sacosatendimentoareia: clienteRumo
          ? param.sacosatendimentoareia
          : itens.reduce(
              (total, item) => total + Number(item.quantidadeareiaitem || 0),
              0
            ),
        itens: clienteRumo ? null : itens,
        idcliente: client,
        justificativas: param.justificativas || [],
      };

      if (objSend.idatendimentoareia) {
        await api.put('pitstop/areia', objSend);
      } else {
        await api.post('pitstop/areia', objSend);
      }

      await close();
      toast.success(t('s.geral.sucessoalteracao'));
    } catch (err) {
      await finalizaGravacao();

      if (err && isArray(err)) {
        toast.error(err[0]);
      }

      await cancelarAcao();

      const validationErrors = {};

      if (err instanceof Yup.ValidationError) {
        err.inner.forEach((error) => {
          validationErrors[error.path] = error.message.replace(
            `${error.path} `,
            ''
          );
        });
        reference.current.setErrors(validationErrors);
      }
    }
  }

  useEffect(() => {
    if (inputRef.current) {
      inputRef.current.focus();
    }
  }, []);

  return (
    <>
      {isEmpty(initials) ? (
        <div style={{ height: '14rem', width: '50rem' }}>
          <Spinner loading />
        </div>
      ) : (
        <>
          <Form ref={reference} initialData={initials} onSubmit={handleSubmit}>
            <Container>
              <Hidden name="idatendimentoareia" />
              <Hidden name="idatendimento" defaultValue={idatendimento} />
              <Hidden name="idposto" defaultValue={idposto} />
              <Input
                name="codigoatendimento"
                label={t('s.cadastro.pitstop.codigoatendimento')}
                disabled={true}
                defaultValue={co}
              />
              <Select
                name="atendeuatendimentoareia"
                label={t('s.cadastro.pitstop.atendeuatendimentoareia')}
                options={optionsAtendimento}
                inputRef={inputRef}
                style={{ marginTop: 8 }}
                getValue={(value) => setAtendeu(value)}
              />
              {clienteRumo === false && (
                <div style={{ marginTop: 8, marginBottom: 10 }}>
                  <Autocomplete
                    name="codigousuario"
                    label="nomecodigousuario"
                    url="usuarios"
                    unique="codigousuario"
                    title={t('s.operacao.abastecimento.codigousuario')}
                    disableClearable={true}
                    params={{
                      filterusrposto: idposto,
                      edicao: Boolean(initials?.idatendimentoareia),
                    }}
                    style={{ marginTop: 8, marginBottom: 10 }}
                  />
                </div>
              )}
              {atendeu === 'S' && (
                <>
                  <DateTime
                    label={t('s.geral.opcoes.datahorainicial')}
                    name="datainicioatendimentoareia"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 8 }}
                  />
                  <DateTime
                    label={t('s.geral.opcoes.datahorafinal')}
                    name="datafimatendimentoareia"
                    serialize={(value) =>
                      value.set({ second: 0 }).format('DD/MM/YYYY HH:mm:ss')
                    }
                    time
                    nullable
                    style={{ marginTop: 16 }}
                  />
                  {clienteRumo === true && (
                    <Input
                      label={t('s.cadastro.pitstop.sacosatendimentoareia')}
                      name="sacosatendimentoareia"
                      style={{ marginTop: 16 }}
                    />
                  )}
                  {clienteRumo === false && (
                    <>
                      <Table size="small" style={{ marginTop: 16 }}>
                        <TableHead>
                          <TableRow>
                            <TableCell>
                              {t('s.cadastro.pitstop.tipoareia')}
                            </TableCell>
                            <TableCell>
                              <>
                                {t(
                                  's.relatorio.lubrificacaolistagem.quantidade'
                                )}
                                {' ou '}
                                <br />
                                {t(
                                  'abastecidos.operacao.geolocalizacao.tempoabastecimento'
                                )}
                                {' (segundos)'}
                              </>
                            </TableCell>
                            <TableCell>{t('s.geral.pesounitario')}</TableCell>
                            <TableCell>{t('s.geral.pesototal')}</TableCell>
                            <TableCell align="right">
                              <IconButton size="small" onClick={handleAddItem}>
                                <AddIcon fontSize="small" />
                              </IconButton>
                            </TableCell>
                          </TableRow>
                        </TableHead>
                        <TableBody>
                          {areiaItens.map((item, index) => {
                            const calculo = calculaItem(item);

                            return (
                              <TableRow key={`areia-item-${index}`}>
                                <TableCell>
                                  <TextField
                                    select
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={item.idpostotipoareia || ''}
                                    onChange={(event) =>
                                      handleChangeItem(
                                        index,
                                        'idpostotipoareia',
                                        event.target.value
                                      )
                                    }
                                  >
                                    {tiposAreia.map((tipo) => (
                                      <MenuItem
                                        key={tipo.idpostotipoareia}
                                        value={tipo.idpostotipoareia}
                                      >
                                        {tipo.silotipoareia === 'S'
                                          ? tipo.nomepostotipoareia
                                          : tipo.descricaotipoareia}
                                      </MenuItem>
                                    ))}
                                  </TextField>
                                </TableCell>
                                <TableCell>
                                  <TextField
                                    variant="outlined"
                                    size="small"
                                    fullWidth
                                    value={item.quantidadeareiaitem ?? ''}
                                    onChange={(event) =>
                                      handleChangeItem(
                                        index,
                                        'quantidadeareiaitem',
                                        event.target.value
                                      )
                                    }
                                  />
                                </TableCell>
                                <TableCell>
                                  {Number(
                                    calculo.pesounitarioareiaitem || 0
                                  ).toFixed(2)}
                                </TableCell>
                                <TableCell>
                                  {Number(
                                    calculo.pesototalareiaitem || 0
                                  ).toFixed(2)}
                                </TableCell>
                                <TableCell align="right">
                                  <IconButton
                                    size="small"
                                    onClick={() => handleRemoveItem(index)}
                                  >
                                    <DeleteIcon fontSize="small" />
                                  </IconButton>
                                </TableCell>
                              </TableRow>
                            );
                          })}
                        </TableBody>
                        <TableRow>
                          <TableCell>
                            <strong>{t('s.geral.total')}</strong>
                          </TableCell>
                          <TableCell />
                          <TableCell />
                          <TableCell>
                            <strong>
                              {Number(totalPesoAreia || 0).toFixed(2)}
                            </strong>
                          </TableCell>
                          <TableCell />
                        </TableRow>
                      </Table>
                      <Button
                        size="small"
                        color="primary"
                        startIcon={<AddIcon />}
                        onClick={handleAddItem}
                        style={{ marginTop: 8, alignSelf: 'flex-start' }}
                      >
                        {t('s.geral.botaoincluir')}
                      </Button>
                    </>
                  )}
                </>
              )}
              {atendeu === 'N' && (
                <>
                  <ComboboxMultiple
                    name="justificativas"
                    label="descricaoobservacao"
                    url="pitstop/justificativa"
                    unique="idobservacaoatendimento"
                    title={t(
                      's.relatorio.alteracaomodooperacional.justificativa'
                    )}
                    params={{
                      wherefield: ['tipoatendimento'],
                      typewherefield: ['EQUALS'],
                      valuewherefield: ['3'],
                      pesquisalista: ['true'],
                    }}
                    style={{ marginBottom: 12 }}
                  />
                </>
              )}
              <Input
                name="observacaoareia"
                label={t('s.cadastro.pitstop.observacaoatendimento')}
                multiline
                rows={3}
                inputProps={{ maxLength: 500 }}
                style={{ marginTop: 10 }}
              />
            </Container>
          </Form>
        </>
      )}
    </>
  );
}
